const EMPTY_CALENDAR_RESULTS = {
  aggregations: {},
  hits: [],
  pageCount: 1,
  currentPage: 1,
  resultCount: 0,
};

export const toUnixTimestamp = (dateValue, endOfDay = false) => {
  if (!dateValue) return undefined;

  const [year, month, day] = String(dateValue).split('-').map(Number);
  if (!year || !month || !day) return undefined;

  const nextDate = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day, 0, 0, 0);

  return Math.floor(nextDate.getTime() / 1000);
};

export const buildCalendarSearchURL = (endpoint, data) => {
  const queryParams = new URLSearchParams();
  const pageLimit = data?.limit || 9;
  const from = ((data?.page || 1) - 1) * pageLimit;

  queryParams.append('size', pageLimit);
  queryParams.append('from', from);

  if (data?.term) {
    queryParams.append('q', data.term);
  }

  const dateFrom = toUnixTimestamp(data?.filters?.dateRange?.[0], false);
  const dateTo = toUnixTimestamp(data?.filters?.dateRange?.[1], true);

  if (dateFrom) {
    queryParams.append('dateFrom', dateFrom);
  }
  if (dateTo) {
    queryParams.append('dateTo', dateTo);
  }

  const appendFacet = (key, values) => {
    if (!values) return;
    const normalizedValues = Array.isArray(values) ? values : [values];
    normalizedValues.filter(Boolean).forEach(value => queryParams.append(key, value));
  };

  appendFacet('category', data?.filters?.category);
  appendFacet('audience', data?.filters?.audiences);
  appendFacet('location', data?.filters?.location);

  return `${endpoint}?${queryParams.toString()}`;
};

export const normalizeCalendarResponse = (payload) => {
  const pagination = payload?.pagination || {};
  const rawAggregations = payload?.aggregations || {};

  const normalizeAggregationList = (aggregation) => {
    const source = Array.isArray(aggregation)
      ? aggregation
      : (Array.isArray(aggregation?.buckets) ? aggregation.buckets : []);

    return source
      .map(item => ({
        value: item?.value || item?.key || '',
        count: item?.count ?? item?.doc_count ?? 0,
      }))
      .filter(item => Boolean(item.value));
  };

  const normalizedAggregations = {
    categories: normalizeAggregationList(rawAggregations.categories || rawAggregations.category),
    audiences: normalizeAggregationList(rawAggregations.audiences || rawAggregations.audience),
    locations: normalizeAggregationList(rawAggregations.locations || rawAggregations.location),
  };

  return {
    aggregations: normalizedAggregations,
    hits: payload?.results || [],
    resultCount: pagination.total || 0,
    pageCount: pagination.totalPages || 1,
    currentPage: pagination.currentPage || 1,
  };
};

export const fetchCalendarResults = async (endpoint, data) => {
  try {
    const url = buildCalendarSearchURL(endpoint, data);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Calendar search failed with status ${response.status}`);
    }

    const payload = await response.json();
    return normalizeCalendarResponse(payload);
  } catch (err) {
    console.log(err.message);
    return EMPTY_CALENDAR_RESULTS;
  }
};

export const emptyCalendarResults = EMPTY_CALENDAR_RESULTS;